<template>
  <span>{{ formattedNumber }}</span>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

interface Props {
  value: number;
  duration?: number;
  decimals?: number;
}

const props = withDefaults(defineProps<Props>(), {
  duration: 1000,
  decimals: 0,
});

const displayNumber = ref<number>(props.value ?? 0);
let animationFrameId: number | null = null;

const formattedNumber = computed((): string =>
  displayNumber.value.toFixed(props.decimals)
);

const animate = (start: number, end: number, startTime: number): void => {
  const now = performance.now();
  const elapsed = now - startTime;

  if (elapsed < props.duration) {
    const progress = elapsed / props.duration;
    const easeOutQuad = 1 - (1 - progress) ** 2;

    displayNumber.value = start + (end - start) * easeOutQuad;
    animationFrameId = requestAnimationFrame(() =>
      animate(start, end, startTime)
    );
  } else {
    displayNumber.value = end;
  }
};

const startAnimation = (newValue: number): void => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
  }

  const startValue = displayNumber.value;
  animate(startValue, newValue, performance.now());
};

watch(
  () => props.value,
  (newValue) => {
    if (newValue != null) {
      startAnimation(newValue);
    }
  },
  { immediate: true }
);

onMounted(() => {
  displayNumber.value = props.value ?? 0;
});

onUnmounted(() => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
  }
});
</script>
